import React, { useState, useEffect } from "react";
import ReactHtmlParser from "react-html-parser";
import { connect } from "react-redux";
import "./Information.scss";
import { setAllObjectConsentSwitch, setScreen } from "../../../redux/actions";
import {
  switchScreenButton,
  switchScreenButton2,
} from "../../../scripts/lib/handlers";
import getNameFromUrl from "../../../scripts/lib/getNameFromUrl";
import Accordion from "../../Accordion/Accordion";

const InformationTags = {
  PublisherName: "[[publisherName]]",
  ThirdPartiesUrl: "[[thirdPartiesUrl]]",
  LegitimateInterestUrl: "[[legitimateInterestUrl]]",
  Purposes: "[[purposes]]",
  LegitimateInterest: "[[legitimateInterest]]",
  Features: "[[features]]",
  TopSection: "[[topSection]]",
  BottomSection: "[[bottomSection]]",
  TotalVendors: "[[totalVendors]]",
  PartnersScreen: "[[partnersScreen]]",
  CookiePolicyUrl: "[[cookiePolicyUrl]]",
};

const filters = [
  InformationTags.Purposes,
  InformationTags.LegitimateInterest,
  InformationTags.Features,
];

const Information = (props) => {
  const filtersAllowed = { specialPurposes: 1, specialFeatures: 1 };
  const { configuration } = props;
  const [totalVendors, setTotalVendors] = useState(0);
  let { currentLanguage } = props;

  if (
    currentLanguage &&
    configuration?.domesticLanguages?.[currentLanguage.code + "-dom"]
  ) {
    currentLanguage =
      configuration.domesticLanguages[currentLanguage.code + "-dom"];
  }
  const { intro, thirdParties, legitimateInterest } = currentLanguage.text;
  // props.setAllObjectConsentSwitch(true);

  // Function to navigate to the "partners" screen
  const navigateToPartnersScreen = () => {
    props.setCurrentScreen(4);
  };

  // Update partnersUrl to use the partners argument
  const partnersUrl = (index, partners) => {
    const localizedPartners = currentLanguage.text.partners || "IAB";
    return (
      <span
        className="link"
        key={`intro_${index}`}
        onClick={() => switchScreenButton2(configuration)}
      >
        {partners || localizedPartners}
      </span>
    );
  };

  const thirdPartiesUrl = (index) => {
    return (
      <span
        className="link"
        key={`intro_${index}`}
        onClick={() => switchScreenButton2(configuration)}
      >
        {thirdParties || "third parties"}
      </span>
    );
  };

  //Cookie Policy link for Italian CMP
  const cookiePolicyUrl = (index) => {
    const handleCookiePolicyClick = () => {
      window.location.href = "http://www.webads.it/cookies/";
    };

    return (
      <span
        className="link"
        key={`intro_${index}`}
        onClick={handleCookiePolicyClick}
      >
        {"Cookie Policy"}
      </span>
    );
  };

  const fetchVendorList = async () => {
    try {
      const response = await fetch(
        "https://nojazz.eu/consent/cmpv3/vendor-list.json"
      );
      const data = await response.json();

      const vendors = data?.vendors;
      if (vendors) {
        const numberOfVendors = Object.keys(vendors).length;
        setTotalVendors(numberOfVendors);
        // data = adjustLastUpdatedTime(data);
      }
    } catch (error) {
      console.error("Error fetching vendor list:", error);
    }
  };

  // Call the fetchVendorList function once when the component mounts
  useEffect(() => {
    fetchVendorList();
  }, []);

  const adjustLastUpdatedTime = (data) => {
    if (data?.lastUpdated) {
      let date = new Date(data.lastUpdated);
      date.setHours(0, 0, 0, 0);
      data.lastUpdated = date.toISOString();
    }
    return data;
  };

  const legitimateInterestUrl = (index) => {
    const { legitimateInterestUrl } = currentLanguage;
    return (
      <a
        href={
          legitimateInterestUrl ||
          "https://eur-lex.europa.eu/legal-content/EN/TXT/HTML/?uri=CELEX:32016R0679&amp;from=EN#d1e1489-1-1"
        }
        target="_blank"
        rel="noopener noreferrer"
        key={`intro_${index}`}
      >
        {legitimateInterest || "legitimate interest"}
        <span className="icon-external-link" />
      </a>
    );
  };

  const renderFilterButtons = (filterName) => {
    if (currentLanguage?.vendorList?.[filterName]) {
      try {
        return (
          <ol
            key={`ol_${filterName}`}
            type={filtersAllowed[filterName] === 1 ? "A" : "1"}
          >
            {currentLanguage.vendorList[filterName].map((object) => (
              <li key={`${filterName}_${object.id}`}>{object.name}</li>
            ))}
          </ol>
        );
      } catch (error) {
        return null;
      }
    } else {
      return null;
    }
  };

  const renderSection = (section) => {
    let htmlSection = props.configuration?.custom?.htmlSection;
    let sectionProperties =
      htmlSection?.[props.currentLanguage.code.toLowerCase()] ||
      htmlSection?.["default"];

    if (sectionProperties) {
      return (
        <div key={`section_${section}`}>
          {ReactHtmlParser(sectionProperties[section])}
        </div>
      );
    } else {
      return null;
    }
  };

  const renderPreface = () => {
    // Regex matches the top section and the sentences up to the accordion section
    let prefaceRegex = new RegExp(
      `${sanitize(InformationTags.TopSection)}.+?${sanitize(
        InformationTags.ThirdPartiesUrl
      )}.+?\\.`,
      "s"
    );
    let preface = intro
      .match(prefaceRegex)?.[0]
      .replace(/(\r\n|\r|\n)+?(?=\r\n|\r|\n)/, "");
    if (preface) {
      return (
        <div className="preface" key="preface">
          {replaceTags(preface)}
        </div>
      );
    } else return null;
  };

  const renderAfterword = () => {
    // fixme: Regex doesn't work if there is no [[purposes]], [[legitimateInterest]] etc. (a.k.a. the accordion section)
    // TODO: Create single regex that gets preface, accordion section, and afterword, by matching groups
    // Regex matches everything but captures the afterword when its preceded by the accordion section
    let afterwordRegex = new RegExp(
      `.*(?:${filters
        .map(sanitize)
        .join("|")})(?:\\r\\n|\\r|\\n)*(?<afterword>.*)`,
      "s"
    );
    let afterword = intro.match(afterwordRegex)?.groups.afterword;

    if (afterword) {
      return (
        <div className="afterword" key="afterword">
          {afterword
            .split(/(?:\r\n|\r|\n)/)
            .filter(Boolean)
            .map((paragraph, index) => (
              <p key={`afterword_paragraph_${index}`}>
                {replaceTags(paragraph)}
              </p>
            ))}
        </div>
      );
    } else return null;
  };

  const generateAccordionItem = (filter) => {
    // Get appropriate filter name
    let filterName;
    switch (filter) {
      case InformationTags.Purposes:
        filterName = "purposes";
        break;
      case InformationTags.LegitimateInterest:
        filterName = "specialPurposes";
        break;
      case InformationTags.Features:
        filterName = "specialFeatures";
        break;
      default:
        filterName = "";
    }
    let filterLabel = intro.match(
      new RegExp(
        `(?:\\r\\n|\\r|\\n)?(?<accordionLabel>.+(?=(?:\\r\\n|\\r|\\n)${sanitize(
          filter
        )}))`
      )
    )?.groups.accordionLabel;
    let filterContent = renderFilterButtons(filterName);

    // Return object that contains both the title and content
    return { title: replaceTags(filterLabel), content: filterContent };
  };

  const renderAccordion = () => {
    let accordionItems = filters
      .filter((string) => intro.includes(string))
      .map(generateAccordionItem);

    return <Accordion items={accordionItems} key="accordion" />;
  };

  const getInformation = () => {
    let preface = renderPreface();
    let accordion = renderAccordion();
    let afterword = renderAfterword();

    return [preface, accordion, afterword];
  };

  const replaceTags = (taggedString) => {
    let parsedHtml = taggedString.match(/\[*[^[\]]+]*/g);
    for (let i = 0; i < parsedHtml?.length; i++) {
      switch (parsedHtml[i]) {
        case InformationTags.PublisherName:
          parsedHtml[i] =
            props.configuration?.publisherName ||
            getNameFromUrl(window.location?.ancestorOrigins?.[0]);
          break;
        case InformationTags.ThirdPartiesUrl:
          parsedHtml[i] = thirdPartiesUrl(i);
          break;
        case InformationTags.LegitimateInterestUrl:
          parsedHtml[i] = legitimateInterestUrl(i);
          break;
        case InformationTags.TopSection:
          parsedHtml[i] = renderSection("top");
          break;
        case InformationTags.BottomSection:
          parsedHtml[i] = renderSection("bottom");
          break;
        case InformationTags.TotalVendors:
          parsedHtml[i] = totalVendors;
          break;
        case InformationTags.PartnersScreen:
          parsedHtml[i] = partnersUrl(i);
          break;
        case InformationTags.CookiePolicyUrl:
          parsedHtml[i] = cookiePolicyUrl(i);
          break;
        default:
          ReactHtmlParser(parsedHtml[i]);
      }
    }
    return parsedHtml;
  };

  if (currentLanguage.text) {
    return (
      <div id="information">
        <div className="pre">{getInformation()}</div>
      </div>
    );
  } else {
    return null;
  }
};

const mapStateToProps = (state) => {
  return { ...state };
};

const sanitize = (string) => string.replace(/[-/\\^$*+?.()|[\]{}]/g, "\\$&");

export default connect(mapStateToProps, {
  setScreen,
  setAllObjectConsentSwitch,
})(Information);
